import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  taskingDialog: {
    open: false,
    step: 1,
    orderName: '',
    processingLevel: 'L2A',
    imageFileFormat: 'GeoTIFF',
    // Editable per-order tasking constraints, seeded from the filter sliders
    // when the dialog opens (see TaskingDialog). null = not yet seeded.
    maxOffNadirAngle: null,
    minSunElevation: null,
    isSubmitting: false,
    submitError: null,
  },
  catalogDialog: {
    open: false,
    step: 1,
    orderName: '',
    orderDescription: '',
    processingLevel: 'L2A',
    imageFileFormat: 'GeoTIFF',
    isSubmitting: false,
    submitError: null,
  },
  // Bhoonidhi-role checkout: a trimmed review popup that stages the selected
  // scenes into the Bhoonidhi cart on confirm, then redirects to the portal.
  // Separate from catalogDialog — no order is created in our system.
  bhoonidhiCartDialog: {
    open: false,
    isSubmitting: false,
    error: null,
  },
  confirmationPopup: {
    open: false,
    orderId: null,
    orderName: '',
    constellation: '',
    estimatedDelivery: null,
  },
  orderDetailsPanel: {
    open: false,
    selectedOrderId: null,
  },
  savedDraftModal: {
    open: false,
    selectedDraftId: null,
  },
};

const dialogSlice = createSlice({
  name: 'dialog',
  initialState,
  reducers: {
    openTaskingDialog(state) {
      state.taskingDialog.open = true;
    },
    closeTaskingDialog(state) {
      state.taskingDialog = { ...initialState.taskingDialog };
    },
    setTaskingDialogStep(state, action) {
      state.taskingDialog.step = action.payload;
    },
    setTaskingDialogField(state, action) {
      const { field, value } = action.payload;
      state.taskingDialog[field] = value;
    },
    resetTaskingDialog(state) {
      state.taskingDialog = { ...initialState.taskingDialog };
    },
    openCatalogDialog(state) {
      state.catalogDialog.open = true;
      state.catalogDialog.step = 1;
    },
    closeCatalogDialog(state) {
      state.catalogDialog = { ...initialState.catalogDialog };
    },
    setCatalogDialogStep(state, action) {
      state.catalogDialog.step = action.payload;
    },
    setCatalogDialogField(state, action) {
      const { field, value } = action.payload;
      state.catalogDialog[field] = value;
    },
    resetCatalogDialog(state) {
      state.catalogDialog = { ...initialState.catalogDialog };
    },
    openBhoonidhiCartDialog(state) {
      state.bhoonidhiCartDialog.open = true;
      state.bhoonidhiCartDialog.isSubmitting = false;
      state.bhoonidhiCartDialog.error = null;
    },
    closeBhoonidhiCartDialog(state) {
      state.bhoonidhiCartDialog = { ...initialState.bhoonidhiCartDialog };
    },
    setBhoonidhiCartDialogField(state, action) {
      const { field, value } = action.payload;
      state.bhoonidhiCartDialog[field] = value;
    },
    showConfirmation(state, action) {
      const { orderId, orderName, constellation, estimatedDelivery } = action.payload;
      state.confirmationPopup = {
        open: true,
        orderId,
        orderName,
        constellation,
        estimatedDelivery,
      };
    },
    closeConfirmation(state) {
      state.confirmationPopup = { ...initialState.confirmationPopup };
    },
    openOrderDetailsPanel(state, action) {
      state.orderDetailsPanel.open = true;
      state.orderDetailsPanel.selectedOrderId = action.payload;
    },
    closeOrderDetailsPanel(state) {
      state.orderDetailsPanel = { ...initialState.orderDetailsPanel };
    },
    openSavedDraftModal(state, action) {
      state.savedDraftModal.open = true;
      state.savedDraftModal.selectedDraftId = action.payload;
    },
    closeSavedDraftModal(state) {
      state.savedDraftModal = { ...initialState.savedDraftModal };
    },
  },
});

export const {
  openTaskingDialog,
  closeTaskingDialog,
  setTaskingDialogStep,
  setTaskingDialogField,
  resetTaskingDialog,
  openCatalogDialog,
  closeCatalogDialog,
  setCatalogDialogStep,
  setCatalogDialogField,
  resetCatalogDialog,
  openBhoonidhiCartDialog,
  closeBhoonidhiCartDialog,
  setBhoonidhiCartDialogField,
  showConfirmation,
  closeConfirmation,
  openOrderDetailsPanel,
  closeOrderDetailsPanel,
  openSavedDraftModal,
  closeSavedDraftModal,
} = dialogSlice.actions;

export default dialogSlice.reducer;
